
import * as L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
L.PM.setOptIn(true);
import { LeafletDirective } from '@bluehalo/ngx-leaflet';
import type { MultiPolygon } from 'geojson';
import { LeafletUtils } from './leaflet.utils';
import { of } from 'rxjs';
import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Directive, EventEmitter, Inject, Input, NgZone, Output } from '@angular/core';

@Directive({
  selector: '[mtxSingleShapeEditor]',
  standalone: false,
})
export class SingleShapeEditorDirective implements OnChanges, OnDestroy, OnInit {

  /** shape being modified */
  @Input()
  public mtxSingleShapeEditor?: MultiPolygon;

  /** notify when shape changes */

  @Output()
  public mtxSingleShapeEditorChange = new EventEmitter<MultiPolygon | undefined>();

  /** shape color to use, hex #00bf00 */
  @Input()
  public mtxSingleShapeEditorColor = '#3c3c3e'; // #00bf00

  private readonly allLayers: L.LayerGroup = new L.LayerGroup([], { pmIgnore: false });

  private mapSetupDone = false;


  public constructor(
    @Inject(LeafletDirective) private readonly leafletDirective: LeafletDirective,
    @Inject(NgZone) private readonly zone: NgZone,
  ) {
    const prexistingMap = this.leafletDirective.getMap();
    const mapReady = prexistingMap ? of(prexistingMap) : this.leafletDirective.mapReady;

    mapReady.subscribe(map => this.setupMap(map));
  }


  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.mtxSingleShapeEditor) {
      this.updateShapeOnMap(this.mtxSingleShapeEditor);
    }

    if (changes.mtxSingleShapeEditorColor) {
      this.resetColors(this.leafletDirective?.getMap());
    }
  }

  /**
   * Fallback: if the mapReady subscription in the constructor missed the event
   * (can happen when Angular initializes directives via dynamic component creation),
   * set up PM controls now that ngOnInit guarantees the map has been created.
   */
  public ngOnInit(): void {
    if (!this.mapSetupDone) {
      const map = this.leafletDirective.getMap();
      if (map) {
        this.setupMap(map);
      }
    }
  }

  public ngOnDestroy(): void {
    this.allLayers?.remove();
  }

  private resetColors(map?: L.Map): void {
    if (!map) {
      return;
    }

    map.pm.setPathOptions({
      color: this.mtxSingleShapeEditorColor,
      fillColor: this.mtxSingleShapeEditorColor,
    });

    map.pm.setGlobalOptions({
      layerGroup: this.allLayers,
      templineStyle: {
        color: this.mtxSingleShapeEditorColor,
        fillColor: this.mtxSingleShapeEditorColor,
        radius: 5,
      },
      hintlineStyle: {
        color: this.mtxSingleShapeEditorColor,
        fillColor: this.mtxSingleShapeEditorColor,
      },
    });

    this.allLayers.getLayers().forEach(layer => {
      if (layer instanceof L.Polygon) {
        layer.setStyle({
          fillColor: this.mtxSingleShapeEditorColor,
          color: this.mtxSingleShapeEditorColor,
        });
      }
    });
  }

  private updateShapeOnMap(shape?: MultiPolygon): void {
    if (!this.leafletDirective?.getMap()) {
      return;
    }

    const editAfterReset = this.leafletDirective?.getMap().pm.globalEditModeEnabled();
    this.allLayers.clearLayers();

    if (editAfterReset) {
      this.leafletDirective?.getMap().pm.enableGlobalEditMode();
    }

    if (shape === undefined) {
      return;
    }

    const polys = LeafletUtils.geojsonMultiPolygonToLeafletPolygons(shape);
    polys.forEach(poly => this.setupLeafletPolygon(poly));
  }

  private setupLeafletPolygon(poly: L.Polygon): void {
    poly.setStyle({
      pmIgnore: false,
      fillColor: this.mtxSingleShapeEditorColor,
      color: this.mtxSingleShapeEditorColor,
    });

    L.PM.reInitLayer(poly);
    poly.addTo(this.allLayers);
    poly.on('pm:edit', () => this.updateShapeFromMap());
    poly.on('pm:remove', () => this.updateShapeFromMap());
  }


  private updateShapeFromMap(): void {
    const mp = LeafletUtils.leafletLayerGroupPolysToMultiPolygon(this.allLayers);
    this.mtxSingleShapeEditor = mp;
    this.mtxSingleShapeEditorChange.emit(mp);
  }


  private setupMap(map: L.Map): void {
    if (this.mapSetupDone) {
      return;
    }
    this.mapSetupDone = true;

    if (map.options.pmIgnore !== false) {
      throw new Error('pmIgnore must be false on map options');
    }

    map.pm.addControls({
      position: 'topleft',
      drawPolyline: false,
      drawPolygon: true,
      drawCircle: true,
      drawRectangle: false,
      drawMarker: false,
      drawCircleMarker: false,
      drawText: false,
      dragMode: true,
      editMode: true,
      cutPolygon: true,
      removalMode: true,
    });

    map.pm.setPathOptions({
      pmIgnore: false,
    });

    this.resetColors(this.leafletDirective?.getMap());

    map.on('pm:create', evt => {
      if (evt.layer instanceof L.Circle) {
        // Circle is not a polygon — approximate it with a 64-point polygon and discard the circle.
        evt.layer.remove();
        const poly = LeafletUtils.circleToPolygon(evt.layer);
        this.setupLeafletPolygon(poly);
        this.leafletDirective?.getMap()?.pm.enableGlobalEditMode();
        this.updateShapeFromMap();
      } else if (evt.layer instanceof L.Polygon) {
        this.setupLeafletPolygon(evt.layer);
        this.leafletDirective?.getMap()?.pm.enableGlobalEditMode();
        this.updateShapeFromMap();
      }
    });

    map.addLayer(this.allLayers);

    this.updateShapeOnMap(this.mtxSingleShapeEditor);

    if (this.mtxSingleShapeEditor) {
      const bounds = LeafletUtils.multipolygonToLeafletBounds(this.mtxSingleShapeEditor);
      this.leafletDirective?.getMap()?.fitBounds(bounds);
    }
  }

}
